import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { getStyleName } from 'site-modules/shared/utils/features-specs/utils';
import { BuildPriceEntities, BuildPriceModel, BuildPricePaths } from 'client/data/models/build-price';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import {
  buildNextLink,
  getStyleIdList,
  transformStylesListToTrimList,
} from 'site-modules/shared/utils/build-price/build-price';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { CompareModels } from 'site-modules/build-price/components/compare-models/compare-models';
import { LinksList } from 'site-modules/build-price/components/links-list/links-list';

import './style-select.scss';

function StyleSelectUI({ buildPriceFilter, pathname, stylesList = null, isMobile = false }) {
  if (!stylesList) {
    return null;
  }

  const isSingleStyle = stylesList.length === 1;

  const trimOptionsList = transformStylesListToTrimList(stylesList).map(({ trimName, trimList, baseMSRP }) => {
    const text = (
      <Fragment>
        <div className="heading-5 pe-1">{trimName}</div>
        {!!baseMSRP && (
          <div className="small text-nowrap">
            from <strong className="heading-5">{getPriceString(baseMSRP)}</strong>
          </div>
        )}
      </Fragment>
    );

    return trimList.length > 1
      ? {
          text,
          subList: trimList.map(({ name, id, price }) => {
            const { engine, style } = getStyleName(name);

            return {
              url: buildNextLink(buildPriceFilter, { styleId: id }, pathname),
              text: (
                <Fragment>
                  <div className="pe-1">
                    <div className="medium fw-medium mb-0_25">{style.replace(trimName, '')}</div>
                    <div className="small">{engine}</div>
                  </div>
                  {!!get(price, 'baseMSRP') && (
                    <div className="small text-nowrap">
                      from <strong className="heading-6">{getPriceString(price.baseMSRP)}</strong>
                    </div>
                  )}
                </Fragment>
              ),
              trackingId: 'build_price_select_from_model_list',
              trackingValue: style,
            };
          }),
          trackingId: 'trim_select',
          trackingValue: trimName,
        }
      : {
          text,
          url: buildNextLink(buildPriceFilter, { styleId: trimList[0].id }, pathname),
          trackingId: 'build_price_select_from_model_list',
          trackingValue: getStyleName(trimList[0].name).style,
        };
  });

  const nextStepButton = stylesList.length === 1 && (
    <Button
      tag="a"
      color="primary-b"
      size="lg"
      className={classnames(
        'add-options-btn px-2 py-1 py-md-0_5 text-transform-none d-flex justify-content-center align-items-center',
        {
          'w-100 border-0': isMobile,
        }
      )}
      href={buildNextLink(buildPriceFilter, { styleId: stylesList[0].id }, pathname)}
      data-tracking-id="build_price_select_from_model_list"
      data-tracking-value={getStyleName(stylesList[0].name).style}
    >
      Add Options <i className="icon-arrow-right4 medium ms-0_75" aria-hidden />
    </Button>
  );

  return (
    <Row
      tag={ScrollElement}
      id="style-select"
      className="style-select justify-content-center"
      data-tracking-parent="step2_modellist"
    >
      <Col xs={12} lg={9}>
        <div className="d-flex justify-content-between mb-1_5">
          <h2 className="heading-4 mb-0" data-test="step-heading">
            {isSingleStyle ? 'Available trim' : 'Select a trim'}
          </h2>
          {stylesList.length > 1 ? (
            <CompareModels
              styleIdList={getStyleIdList(stylesList)}
              styleFilter={buildPriceFilter.styleFilter}
              buttonClassName="text-primary-darker"
              hasArrow={false}
              isMobile={isMobile}
            />
          ) : (
            !isMobile && nextStepButton
          )}
        </div>
        <LinksList
          list={trimOptionsList}
          listClassName="styles-list"
          itemClassName="style-option mb-1"
          linkClassName="card text-gray-darker flex-row justify-content-between align-items-center px-0_75 px-md-1 py-1"
          subItemClassName="mb-1 d-inline-block w-100"
          subListClassName="mx-1 mt-0_5 px-1 pt-1 bg-gray-extra-light"
          keyPrefix="style"
        />
        {isSingleStyle && (
          <div className="mb-3_5 medium text-gray-dark">There is only one trim available for this model.</div>
        )}
        {isMobile && nextStepButton}
      </Col>
    </Row>
  );
}

StyleSelectUI.propTypes = {
  buildPriceFilter: BuildPriceEntities.BuildPriceFilter.isRequired,
  pathname: PropTypes.string.isRequired,
  stylesList: PropTypes.arrayOf(BuildPriceEntities.Style),
  isMobile: PropTypes.bool,
};

export const propsAreEqual = (prevProps, { stylesList }) => !stylesList;
export const StyleSelect = connectToModel(React.memo(StyleSelectUI, propsAreEqual), {
  stylesList: bindToPath(
    ({ buildPriceFilter, modelYear: { makeSlug, modelSlug, year } }) =>
      BuildPricePaths.buildStylesListPath({
        make: makeSlug,
        model: modelSlug,
        submodel: makeNiceName(get(buildPriceFilter, 'styleFilter.submodel')),
        year,
      }),
    BuildPriceModel
  ),
});
